import React, { forwardRef } from 'react';
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}
const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-to-r from-[#1BACA6] to-[#1890C8] text-white hover:from-[#17B8B2] hover:to-[#1A9ED8] shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 dark:shadow-cyan-900/30 active:scale-[0.97]',
  secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-white/[0.07] dark:text-gray-100 dark:hover:bg-white/[0.12] dark:border dark:border-white/[0.06] active:scale-[0.97]',
  danger: 'bg-red-500 text-white hover:bg-red-600 shadow-md shadow-red-500/20 dark:shadow-red-900/30 active:scale-[0.97]',
  ghost: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.07] active:scale-[0.97]',
  outline: 'border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/[0.12] dark:text-gray-200 dark:hover:bg-white/[0.06] active:scale-[0.97]'
};
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3.5 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-2.5 text-sm gap-2'
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  children,
  ...props
}, ref) => {
  return <button ref={ref} disabled={disabled || isLoading} className={`
          inline-flex items-center justify-center font-medium rounded-xl
          transition-all duration-200 ease-out
          focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900
          disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none
          select-none
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `} {...props} data-id="element-5377">
        
        {isLoading ? <svg className="animate-spin h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" data-id="element-5378">
          
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5379" />
          
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-5380" />
          
          </svg> : leftIcon ? <span className="flex-shrink-0" data-id="element-5381">{leftIcon}</span> : null}
        {children}
        {rightIcon && !isLoading && <span className="flex-shrink-0" data-id="element-5382">{rightIcon}</span>}
      </button>;
});
Button.displayName = 'Button';